import { useEffect, useRef, useState } from 'react';
import { Button, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import DialogWithEnterAction from '../../components/DialogWithEnterAction';
import { useAddress } from '../../Hooks/addressHook';
import usePreviewFeatures from '../../Hooks/usePreviewFeatures';
import getStateManager from '../../StateManager';

export default function PreviewFeaturesButton() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const enabled = usePreviewFeatures();
  const [open, setOpen] = useState(false);
  const disable = () => {
    getStateManager().setPreviewFeaturesEnabled(false);
    enqueueSnackbar(t('preview_features_disabled'), { variant: 'info' });
  };

  return <>
    <Button color="primary" onClick={enabled ? disable : () => setOpen(true)}>
      {t(enabled ? 'disable_preview_features' : 'enable_preview_features')}
    </Button>
    {open && <PreviewFeaturesDialog onClose={() => setOpen(false)} />}
  </>;
}

function PreviewFeaturesDialog({ onClose }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const address = useAddress();
  const [pin, setPin] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const request = useRef(null);
  useEffect(() => () => request.current?.abort(), []);

  const unlock = async () => {
    if (!pin || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    setPending(true);
    setError(null);
    try {
      const response = await fetch(address.api_url('/api/confirm_pin?' + new URLSearchParams({
        purpose: 'preview_features', pin
      })), { signal: controller.signal });
      if (!response.ok) {
        const message = await response.text();
        if (!controller.signal.aborted) setError(message === 'invalid_pin' ? 'invalid_pin' : 'unknown_error');
        return;
      }
      const result = await response.json();
      if (controller.signal.aborted) return;
      if (result.feature !== 'preview_features') {
        setError('unknown_error');
        return;
      }
      getStateManager().setPreviewFeaturesEnabled(true);
      enqueueSnackbar(t('preview_features_enabled'), { variant: 'info' });
      onClose();
    } catch (_) {
      if (!controller.signal.aborted) setError('unknown_error');
    } finally {
      request.current = null;
      if (!controller.signal.aborted) setPending(false);
    }
  };

  return <DialogWithEnterAction open onClose={onClose} onEnterAction={unlock}
    enterActionDisabled={pending || !pin} aria-labelledby="preview-features-title">
    <DialogTitle id="preview-features-title">{t('enable_preview_features')}</DialogTitle>
    <DialogContent>
      <Typography>{t('preview_features_description')}</Typography>
      <TextField autoFocus margin="dense" id="preview-features-pin" label={t('preview_features_pin')}
        type="password" fullWidth autoComplete="off" value={pin} disabled={pending}
        inputProps={{ inputMode: 'numeric' }} error={Boolean(error)} helperText={error ? t(error) : undefined}
        onChange={(event) => { setPin(event.target.value); setError(null); }} />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary">{t('cancel')}</Button>
      <Button onClick={unlock} color="primary" disabled={pending || !pin}>
        {t(pending ? 'loading' : 'enable_preview_features')}
      </Button>
    </DialogActions>
  </DialogWithEnterAction>;
}
